import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { createTw } from "react-pdf-tailwind";
import { convertToUpper } from "../../utils/convertToUpper";

const tw = createTw({
  theme: {
    fontFamily: { sans: "Helvetica" },
    extend: {
      colors: {
        brand: "#003366",
        accent: "#0056b3",
        headerGray: "#f8f9fa",
        borderGray: "#d1d5db",
        successLight: "#f0fdf4",
        successBorder: "#22c55e",
      },
    },
  },
});

const styles = StyleSheet.create({
  pageContent: {
    padding: 24,
    fontFamily: "Helvetica",
    fontSize: 6,
    lineHeight: 1.2,
    color: "#1f2937",
    textTransform: "uppercase",
  },
  recommendedColumn: {
    backgroundColor: "#f0f9ff",
  },
  signatureBlock: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderStyle: "solid",
    borderRadius: 2,
    overflow: "hidden",
  },
  borderBottomBrand: {
    borderBottomWidth: 2,
    borderBottomColor: "#003366",
    borderBottomStyle: "solid",
  },
  borderAllGray: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderStyle: "solid",
  },
  textWhite: {
    color: "#ffffff",
  },
  textDark: {
    color: "#1f2937",
  },
  textGray: {
    color: "#4b5563",
  },
  bgBrand: {
    backgroundColor: "#003366",
  },
  bgAccent: {
    backgroundColor: "#0056b3",
  },
  bgGray50: {
    backgroundColor: "#f8f9fa",
  },
  bgGray100: {
    backgroundColor: "#f3f4f6",
  },
});

const normalizeName = (name) => {
  return (name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
};

const CuadroComparativoPDF = ({ info }) => {
  const providers = info?.proveedores || [];
  const recommendedId = info?.recomendado?.id;

  const currencySymbol =
    info?.currency === "USD"
      ? "$"
      : info?.currency === "EUR"
        ? "€"
        : info?.currency === "Bs."
          ? "Bs."
          : "$";

  return (
    <Document>
      <Page size="LETTER" orientation="landscape" style={styles.pageContent}>
        {/* ENCABEZADO PREMIUM */}
        <View
          style={[
            tw("flex flex-row items-center justify-between mb-4 pb-2"),
            styles.borderBottomBrand,
          ]}
        >
          <View style={tw("w-[25%]")}>
            <Image
              src={`${typeof window !== "undefined" ? window.location.origin : ""}/logoPlastiforte.png`}
              style={{ width: 120, height: 40, objectFit: "contain" }}
            />
          </View>
          <View style={tw("w-[50%] flex flex-col items-center")}>
            <Text
              style={tw(
                "font-bold text-[10px] text-brand uppercase tracking-wider mb-2",
              )}
            >
              CUADRO COMPARATIVO DE PROVEEDOR
            </Text>
            <Text style={tw("text-[7px] text-gray-500 font-bold")}>
              PROCESO DE ADQUISICIONES
            </Text>
          </View>
          <View style={tw("w-[25%] flex flex-col items-end")}>
            <Text style={tw("font-bold text-[7px]")}>
              CÓDIGO:{" "}
              <Text style={tw("font-normal")}>{info?.codigoDoc || "-"}</Text>
            </Text>
            <Text style={tw("font-bold text-[7px]")}>
              VERSIÓN:{" "}
              <Text style={tw("font-normal")}>{info?.versionDoc || "-"}</Text>
            </Text>
          </View>
        </View>

        {/* INFORMACIÓN GENERAL */}
        <View style={tw("flex flex-row mb-4")}>
          <View style={tw("flex-1 bg-gray-50 p-2 rounded mr-4")}>
            <View style={tw("flex flex-row mb-1")}>
              <Text style={{ width: 80, fontWeight: "bold", color: "#4b5563" }}>
                DEPARTAMENTO SOLICITANTE:
              </Text>
              <Text style={tw("font-bold text-brand uppercase")}>
                {convertToUpper(info?.departamento)}
              </Text>
            </View>
            <View style={tw("flex flex-row")}>
              <Text style={{ width: 80, fontWeight: "bold", color: "#4b5563" }}>
                MOTIVO DE LA SOLICITUD:
              </Text>
              <Text style={tw("flex-1 text-gray-800")}>
                {convertToUpper(info?.descripcion)}
              </Text>
            </View>
          </View>

          <View style={tw("w-[200px] bg-gray-50 p-2 rounded mr-4")}>
            <Text style={tw("font-bold text-gray-600 mb-1")}>
              TIPO DE COMPRA: {convertToUpper(info?.tipoCompraActual)}
            </Text>
          </View>

          <View
            style={[
              tw("w-[120px] p-2 rounded items-center justify-center"),
              styles.bgBrand,
            ]}
          >
            <Text
              style={[
                tw("text-[6px] font-bold opacity-80 uppercase"),
                styles.textWhite,
              ]}
            >
              Nro. Importacion
            </Text>
            <Text style={[tw("text-[10px] font-bold"), styles.textWhite]}>
              {convertToUpper(info?.nroPedido)}
            </Text>
          </View>
        </View>

        {/* TABLA DE COMPARACIÓN */}
        <View
          style={[tw("rounded-sm overflow-hidden mb-4"), styles.borderAllGray]}
        >
          {/* Header de la Tabla */}
          <View
            style={[
              tw("flex-row"),
              styles.bgBrand,
              {
                borderBottomWidth: 1,
                borderBottomColor: "#ffffff",
                borderBottomStyle: "solid",
              },
            ]}
          >
            <View
              style={[
                tw("w-[3%] p-1 items-center justify-center"),
                {
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text style={[tw("font-bold"), styles.textWhite]}>#</Text>
            </View>
            <View
              style={[
                tw("w-[4%] p-1 items-center justify-center"),
                {
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text style={[tw("font-bold"), styles.textWhite]}>CANT.</Text>
            </View>
            <View
              style={[
                tw("w-[4%] p-1 items-center justify-center"),
                {
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text style={[tw("font-bold"), styles.textWhite]}>UNID.</Text>
            </View>
            <View
              style={[
                tw("w-[20%] p-1 justify-center"),
                {
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text style={[tw("font-bold ml-1"), styles.textWhite]}>
                DETALLE DEL PRODUCTO / SERVICIO
              </Text>
            </View>

            <View style={tw("flex-1 flex-row")}>
              {providers.map((prov, i) => (
                <View
                  key={i}
                  style={[
                    tw(`flex-1 p-1 items-center justify-center`),
                    styles.bgBrand,
                    i < providers.length - 1
                      ? {
                          borderRightWidth: 1,
                          borderRightColor: "#ffffff",
                          borderRightStyle: "solid",
                        }
                      : {},
                  ]}
                >
                  <Text
                    style={[
                      tw("text-[5px] uppercase opacity-70"),
                      styles.textWhite,
                    ]}
                  >{`PROVEEDOR ${i + 1}`}</Text>
                  <Text style={[tw("text-[7px] font-bold"), styles.textWhite]}>
                    {convertToUpper(prov.nombre)}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Sub-header de Columnas */}
          <View
            style={[
              tw("flex-row font-bold"),
              styles.bgGray100,
              {
                borderBottomWidth: 1,
                borderBottomColor: "#d1d5db",
                borderBottomStyle: "solid",
              },
            ]}
          >
            <View
              style={[
                tw("w-[31%]"),
                {
                  borderRightWidth: 1,
                  borderRightColor: "#d1d5db",
                  borderRightStyle: "solid",
                },
              ]}
            />
            <View style={tw("flex-1 flex-row")}>
              {providers.map((_, i) => (
                <View
                  key={i}
                  style={[
                    tw("flex-1 flex-row"),
                    i < providers.length - 1
                      ? {
                          borderRightWidth: 1,
                          borderRightColor: "#d1d5db",
                          borderRightStyle: "solid",
                        }
                      : {},
                  ]}
                >
                  <Text
                    style={[
                      tw("w-[15%] text-center py-1 text-[4.5px]"),
                      styles.textGray,
                      {
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    CANT
                  </Text>
                  <Text
                    style={[
                      tw("w-[20%] text-center py-1 text-[4.5px]"),
                      styles.textGray,
                      {
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    UNIT
                  </Text>
                  <Text
                    style={[
                      tw("w-[25%] text-center py-1 text-[4.5px]"),
                      styles.textGray,
                      {
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    TOTAL
                  </Text>
                  <Text
                    style={[
                      tw("flex-1 text-center py-1 text-[4.5px]"),
                      styles.textGray,
                    ]}
                  >
                    OBSERVACIONES
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Cuerpo de la Tabla */}
          {info?.items?.map((item, index) => (
            <View
              key={index}
              style={[
                tw("flex-row"),
                index % 2 !== 0 ? styles.bgGray50 : {},
                {
                  borderBottomWidth: 1,
                  borderBottomColor: "#d1d5db",
                  borderBottomStyle: "solid",
                },
              ]}
            >
              <View
                style={[
                  tw("w-[3%] p-1 items-center justify-center"),
                  {
                    borderRightWidth: 1,
                    borderRightColor: "#d1d5db",
                    borderRightStyle: "solid",
                  },
                ]}
              >
                <Text style={styles.textDark}>{item.item}</Text>
              </View>
              <View
                style={[
                  tw("w-[4%] p-1 items-center justify-center"),
                  {
                    borderRightWidth: 1,
                    borderRightColor: "#d1d5db",
                    borderRightStyle: "solid",
                  },
                ]}
              >
                <Text style={styles.textDark}>{item.cantidad}</Text>
              </View>
              <View
                style={[
                  tw("w-[4%] p-1 items-center justify-center"),
                  {
                    borderRightWidth: 1,
                    borderRightColor: "#d1d5db",
                    borderRightStyle: "solid",
                  },
                ]}
              >
                <Text style={styles.textDark}>{item.unidad}</Text>
              </View>
              <View
                style={[
                  tw("w-[20%] p-1"),
                  {
                    borderRightWidth: 1,
                    borderRightColor: "#d1d5db",
                    borderRightStyle: "solid",
                  },
                ]}
              >
                <Text style={[tw("font-medium"), styles.textDark]}>
                  {convertToUpper(item.detalle)}
                </Text>
              </View>

              <View style={tw("flex-1 flex-row")}>
                {item.ofertas.map((oferta, i) => (
                  <View
                    key={i}
                    style={[
                      tw(`flex-1 flex-row p-[2px]`),
                      i < item.ofertas.length - 1
                        ? {
                            borderRightWidth: 1,
                            borderRightColor: "#d1d5db",
                            borderRightStyle: "solid",
                          }
                        : {},
                      oferta.provId === recommendedId
                        ? styles.recommendedColumn
                        : {},
                    ]}
                  >
                    <Text style={[tw("w-[15%] text-center"), styles.textDark]}>
                      {oferta.cantidad}
                    </Text>
                    <Text
                      style={[
                        tw("w-[20%] text-center font-bold"),
                        styles.textDark,
                      ]}
                    >
                      {oferta.cUnit}
                    </Text>
                    <Text
                      style={[tw("w-[25%] text-center font-bold text-brand")]}
                    >
                      {oferta.cTotal}
                    </Text>
                    <Text
                      style={[
                        tw("flex-1 text-[5px] italic px-1"),
                        styles.textGray,
                      ]}
                    >
                      {convertToUpper(oferta.obs, "")}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          ))}

          {/* SECCIÓN DE TOTALES */}
          <View style={[tw("font-bold"), styles.bgGray100]}>
            <View
              style={[
                tw("flex-row"),
                {
                  borderBottomWidth: 1,
                  borderBottomColor: "#d1d5db",
                  borderBottomStyle: "solid",
                },
              ]}
            >
              <View
                style={[
                  tw("w-[31%] p-1 items-end justify-center pr-4"),
                  {
                    borderRightWidth: 1,
                    borderRightColor: "#d1d5db",
                    borderRightStyle: "solid",
                  },
                ]}
              >
                <Text style={[tw("uppercase"), styles.textGray]}>
                  Subtotal Productos
                </Text>
              </View>
              <View style={tw("flex-1 flex-row")}>
                {providers.map((prov, i) => (
                  <View
                    key={i}
                    style={[
                      tw(
                        `flex-1 p-1 items-center ${prov.id === recommendedId ? "bg-blue-50" : ""}`,
                      ),
                      i < providers.length - 1
                        ? {
                            borderRightWidth: 1,
                            borderRightColor: "#d1d5db",
                            borderRightStyle: "solid",
                          }
                        : {},
                    ]}
                  >
                    <Text style={styles.textDark}>
                      {prov.itemsTotal?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </Text>
                  </View>
                ))}
              </View>
            </View>

            {info?.uniqueLogisticCostTypes?.map((type, idx) => (
              <View
                key={idx}
                style={[
                  tw("flex-row"),
                  {
                    borderBottomWidth: 1,
                    borderBottomColor: "#d1d5db",
                    borderBottomStyle: "solid",
                  },
                ]}
              >
                <View
                  style={[
                    tw("w-[31%] p-1 items-end justify-center pr-4"),
                    {
                      borderRightWidth: 1,
                      borderRightColor: "#d1d5db",
                      borderRightStyle: "solid",
                    },
                  ]}
                >
                  <Text style={[tw("uppercase text-[5px]"), styles.textGray]}>
                    {convertToUpper(type)}
                  </Text>
                </View>
                <View style={tw("flex-1 flex-row")}>
                  {providers.map((prov, i) => {
                    const cost = prov.logisticCosts?.find(
                      (c) => c.type === type,
                    );
                    return (
                      <View
                        key={i}
                        style={[
                          tw(
                            `flex-1 p-1 items-center ${prov.id === recommendedId ? "bg-blue-50" : ""}`,
                          ),
                          i < providers.length - 1
                            ? {
                                borderRightWidth: 1,
                                borderRightColor: "#d1d5db",
                                borderRightStyle: "solid",
                              }
                            : {},
                        ]}
                      >
                        <Text style={[tw("font-normal"), styles.textDark]}>
                          {cost?.monto
                            ? Number(cost.monto).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })
                            : "0.00"}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </View>
            ))}

            <View style={[tw("flex-row"), styles.bgBrand]}>
              <View
                style={[
                  tw("w-[31%] p-2 items-end justify-center pr-4"),
                  {
                    borderRightWidth: 1,
                    borderRightColor: "#ffffff",
                    borderRightStyle: "solid",
                  },
                ]}
              >
                <Text style={[tw("text-[8px] font-bold"), styles.textWhite]}>
                  PRECIO TOTAL DE COMPRA
                </Text>
              </View>
              <View style={tw("flex-1 flex-row")}>
                {providers.map((prov, i) => (
                  <View
                    key={i}
                    style={[
                      tw(`flex-1 p-1 items-center justify-center`),
                      prov.id === recommendedId
                        ? styles.bgAccent
                        : styles.bgBrand,
                      i < providers.length - 1
                        ? {
                            borderRightWidth: 1,
                            borderRightColor: "#ffffff",
                            borderRightStyle: "solid",
                          }
                        : {},
                    ]}
                  >
                    <Text
                      style={[tw(`text-[9px] font-bold`), styles.textWhite]}
                    >
                      {prov.total?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}{" "}
                      {currencySymbol}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* LOGÍSTICA Y SELECCIÓN */}
        <View style={tw("flex-row mb-4")}>
          <View
            style={[
              tw("flex-1 rounded p-2 mr-4"),
              { borderWidth: 1, borderColor: "#d1d5db", borderStyle: "solid" },
            ]}
          >
            <Text
              style={[
                tw("font-bold mb-1 pb-1 uppercase"),
                styles.textGray,
                {
                  borderBottomWidth: 1,
                  borderBottomColor: "#f3f4f6",
                  borderBottomStyle: "solid",
                },
              ]}
            >
              Evaluación Logística y Comercial
            </Text>
            <View style={tw("flex-row")}>
              <View style={{ width: 80, fontWeight: "bold" }}>
                <Text style={[tw("mb-1"), styles.textGray]}>
                  VALIDEZ / ENTREGA
                </Text>
                <Text style={[tw("mb-1"), styles.textGray]}>INCOTERMS</Text>
                <Text style={styles.textGray}>CONDICIONES PAGO</Text>
              </View>
              <View style={tw("flex-1 flex-row")}>
                {providers.map((prov, i) => (
                  <View
                    key={i}
                    style={[
                      tw(
                        `flex-1 px-2 ${prov.id === recommendedId ? "bg-blue-50" : ""}`,
                      ),
                      {
                        borderLeftWidth: 1,
                        borderLeftColor: "#f3f4f6",
                        borderLeftStyle: "solid",
                      },
                    ]}
                  >
                    <Text style={[tw("mb-1 font-bold"), styles.textDark]}>
                      {prov.deliveryDate
                        ? new Date(prov.deliveryDate).toLocaleDateString(
                            "es-ES",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            },
                          )
                        : "-"}
                    </Text>
                    <Text style={[tw("mb-1"), styles.textDark]}>
                      {convertToUpper(prov.incoterms)}
                    </Text>
                    <Text style={styles.textDark}>
                      {convertToUpper(prov.paymentTerms)}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View
            style={[
              tw(`w-[30%] rounded p-2`),
              info?.recomendado
                ? { backgroundColor: "#f0fdf4", borderColor: "#22c55e" }
                : { backgroundColor: "#f8f9fa", borderColor: "#d1d5db" },
              { borderWidth: 2, borderStyle: "solid" },
            ]}
          >
            <View style={tw("flex-row items-center mb-1")}>
              <View
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: info?.recomendado ? "#22c55e" : "#9ca3af",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 8,
                }}
              >
                <Text style={[tw("text-[8px] font-bold"), styles.textWhite]}>
                  {info?.recomendado ? "✓" : "?"}
                </Text>
              </View>
              <Text
                style={[
                  tw(`font-bold uppercase`),
                  { color: info?.recomendado ? "#22c55e" : "#6b7280" },
                ]}
              >
                {info?.recomendado
                  ? "OPCIÓN SELECCIONADA"
                  : "OPCIÓN NO DEFINIDA"}
              </Text>
            </View>
            <Text style={[tw("text-[8px] font-bold mb-1"), styles.textDark]}>
              {convertToUpper(info?.recomendado?.nombre, "AÚN NO DEFINIDO")}
            </Text>
            <Text style={[tw("text-[5px] italic"), styles.textGray]}>
              {info?.recomendado
                ? convertToUpper(
                    info.justificacion ||
                      "Opción seleccionada bajo criterios de costo y tiempo de entrega.",
                  )
                : "PENDIENTE DE RECOMENDACIÓN TÉCNICA/COMERCIAL."}
            </Text>
          </View>
        </View>

        {/* BLOQUE DE FIRMAS TIPO SELLO */}
        <View style={tw("flex-grow")} />
        <View style={tw("flex-row")}>
          {info?.firmas?.map((item, i) => (
            <View
              key={i}
              style={[
                tw("flex-1 mr-4"),
                i === info.firmas.length - 1 ? { marginRight: 0 } : {},
                styles.signatureBlock,
              ]}
            >
              <View
                style={[
                  tw("bg-gray-50 p-1"),
                  {
                    borderBottomWidth: 1,
                    borderBottomColor: "#d1d5db",
                    borderBottomStyle: "solid",
                  },
                ]}
              >
                <Text
                  style={[
                    tw("font-bold uppercase text-[5px]"),
                    { color: "#003366" },
                  ]}
                >
                  {convertToUpper(item.firma)}
                </Text>
              </View>
              <View style={tw("p-2 min-h-[60px] justify-between")}>
                <View>
                  <Image
                    src={item.firmaImg}
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "contain",
                      marginBottom: 3,
                      marginTop: -30,
                    }}
                  />
                  <Text
                    style={[
                      tw("font-bold text-[7px]"),
                      styles.textDark,

                      { marginTop: -45 },
                    ]}
                  >
                    {convertToUpper(item.nombre)}
                  </Text>

                  <Text style={[tw("text-[5px] font-bold"), styles.textGray]}>
                    {convertToUpper(item.cargo, "APROBADOR")}
                  </Text>
                </View>

                <View
                  style={[
                    tw("flex flex-row justify-between items-end pt-1 mt-1"),
                    {
                      borderTopWidth: 1,
                      borderTopColor: "#f3f4f6",
                      borderTopStyle: "solid",
                    },
                  ]}
                >
                  <Text style={[tw("text-[5px]"), styles.textGray]}>
                    FECHA: {convertToUpper(item.fecha)}
                  </Text>

                  <View
                    style={{
                      width: 48,
                      height: 2,
                      backgroundColor: "#e5e7eb",
                    }}
                  />
                </View>
              </View>
              {item.observaciones && (
                <View
                  style={[
                    tw("bg-blue-50 p-1"),
                    {
                      borderTopWidth: 1,
                      borderTopColor: "#d1d5db",
                      borderTopStyle: "solid",
                    },
                  ]}
                >
                  <Text style={[tw("text-[5px] font-bold"), styles.textGray]}>
                    OBS:
                  </Text>
                  <Text style={[tw("text-[5px] italic"), styles.textDark]}>
                    {convertToUpper(item.observaciones)}
                  </Text>
                </View>
              )}
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default CuadroComparativoPDF;
